import { CameraCaptureAdapterProvider } from './adapter/CameraCaptureAdapterProvider';
import { BatchPhotoCaptureConfig, BatchPhotoUploadResult } from '@services/camera/CameraTypes';

export class CameraCaptureService {
    public static captureImage(config: any): Promise<any> {
        return CameraCaptureAdapterProvider.get().captureImage(config);
    }

    public static captureImageAndUpload(config: any): Promise<any> {
        return CameraCaptureAdapterProvider.get().captureImageAndUpload(config);
    }

    /**
     * Captures several photos in one native session and uploads each one, in place of reopening the
     * single-photo camera per photo.
     *
     * The session is associate-paced (capture, review, then a sequential upload drain) and the
     * returned promise stays pending for all of it. Gate calls on
     * {@link CameraCaptureService.isBatchCaptureImplemented}, which answers false on environments
     * with no camera bridge (Velocity). Note that it cannot detect a HOST frame older than the
     * batch listener — that case passes the gate and then rejects with
     * `BatchCaptureNotImplementedException`, so handle it as well as gating.
     *
     * Partial failure RESOLVES: photos that failed appear as capture-order positions in
     * {@link BatchPhotoUploadResult.failedIndices} rather than rejecting the batch, so a caller can
     * retry just the failures without re-capturing the successes. An associate cancel also
     * resolves, with {@link BatchPhotoUploadResult.canceled} set and whatever finalized first.
     * Only session-level failures reject (camera unavailable, a session already in progress,
     * unauthorized bucketAlias, session timeout).
     *
     * @param config - per-photo capture settings plus the batch-only fields (`maxPhotos`,
     *  `enableReview`, `followUpInstructions`, `onPhotoUploaded`).
     * @returns the batch outcome.
     */
    public static captureImagesAndUpload(config: BatchPhotoCaptureConfig): Promise<BatchPhotoUploadResult> {
        return CameraCaptureAdapterProvider.get().captureImagesAndUpload(config);
    }

    /**
     * Whether {@link CameraCaptureService.captureImagesAndUpload} will actually attempt a capture,
     * as opposed to rejecting outright. True on the cross-frame path, which implements batch end to
     * end; false permanently on Velocity, which has no camera bridge.
     *
     * This is the branch point for a tool adopting batch capture: check it, and fall back to the
     * existing per-photo loop when it is false. Prefer it over catching the rejection — a real
     * batch session resolves even when individual photos fail, so a `catch` is not where "batch is
     * unavailable" belongs.
     *
     * Synchronous and free: it asks the resolved adapter directly and sends no cross-frame message,
     * so it is safe to call on a render path. That is also its limit — being a property of THIS
     * library's adapter rather than a runtime probe, it cannot report on the host frame. A tool
     * built against a JSLib with batch support but running in a host frame that predates the
     * `cameraBatchCapture` listener sees true here and a `BatchCaptureNotImplementedException`
     * rejection from the call.
     *
     * @returns true when this library can run a batch capture in the current environment.
     */
    public static isBatchCaptureImplemented(): boolean {
        return CameraCaptureAdapterProvider.get().isBatchCaptureImplemented();
    }
}
